import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Radio, CheckCircle2 } from "lucide-react";

const MODES = [
  { value: "simulated", label: "Simulated", desc: "Synthetic activity stream — the public site always looks busy. Nothing is written to your data." },
  { value: "blended", label: "Blended", desc: "Real numbers plus a simulated baseline on top — the simulated share shrinks as genuine volume grows." },
  { value: "real", label: "Real", desc: "Live database numbers only. Flip to this once your real lead volumes are healthy." },
];

export const PublicActivityCard = () => {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/public-activity").then((r) => setMode(r.data.mode)).catch(() => {});
  }, []);

  const save = async (m) => {
    if (m === mode || busy) return;
    setBusy(true);
    try {
      await api.put("/admin/settings/public-activity", { mode: m });
      setMode(m);
      toast.success(`Public site activity switched to ${m} — live within ~20 seconds`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update mode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="public-activity-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center size-8 rounded-md bg-slate-900 text-white"><Radio className="size-4" /></span>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Public site activity</h3>
            <p className="text-xs text-slate-500">Controls the trust banner, live activity page and public counters.</p>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => save(m.value)}
              disabled={busy}
              data-testid={`activity-mode-${m.value}`}
              className={`text-left rounded-xl border p-4 transition-all ${
                mode === m.value ? "border-[#0055FF] bg-[#0055FF]/[0.04] shadow-[0_2px_12px_rgba(0,85,255,0.12)]" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-semibold ${mode === m.value ? "text-[#0055FF]" : "text-slate-900"}`}>{m.label}</span>
                {mode === m.value && <CheckCircle2 className="size-3.5 text-[#0055FF]" />}
              </div>
              <p className="mt-1.5 text-[11.5px] text-slate-500 leading-relaxed">{m.desc}</p>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
